<script>
  import { onMount, afterUpdate, onDestroy } from 'svelte';
  
  export let stores = [];
  export let searchLocation = '';
  export let searchRadius = 30; // Rayon en km
  
  let mapElement;
  let map;
  let markers = [];
  let infoWindow;
  let circle = null; // Pour le cercle de rayon
  let mapInitialized = false;
  let geocoder = null;
  let centerMarker = null; // Marqueur pour le centre de recherche
  
  onMount(() => {
    // On attend que le DOM soit complètement chargé
    setTimeout(() => {
      if (window.google && window.google.maps) {
        initMap();
      } else {
        console.error("Google Maps API n'est pas chargée. Assurez-vous d'avoir inclus l'API Google Maps avec la bonne clé API.");
      }
    }, 0);
  });
  
  afterUpdate(() => {
    if (mapInitialized && map) {
      if (stores.length > 0) {
        updateMarkers();
      }
      
      // Si on a une nouvelle recherche, mettre à jour le cercle de rayon
      if (searchLocation) {
        updateSearchRadius();
      }
    }
  });
  
  onDestroy(() => {
    // Nettoyage: supprimer les marqueurs et références
    if (markers.length > 0) {
      markers.forEach(marker => {
        // Supprimer les écouteurs d'événements
        if (marker && google && google.maps && google.maps.event) {
          google.maps.event.clearInstanceListeners(marker);
        }
        // Supprimer le marqueur de la carte
        if (marker) {
          marker.setMap(null);
        }
      });
    }
    
    // Supprimer le cercle de rayon
    if (circle) {
      circle.setMap(null);
    }
    
    // Supprimer le marqueur central
    if (centerMarker) {
      centerMarker.setMap(null);
    }
    
    markers = [];
    map = null;
    infoWindow = null;
    circle = null;
    centerMarker = null;
  });
  
  function initMap() {
    // Vérifier que l'élément DOM existe
    if (!mapElement) {
      console.error("L'élément DOM pour la carte n'est pas disponible");
      return;
    }
    
    try {
      // Options de la carte
      const mapOptions = {
        center: { lat: 46.603354, lng: 1.888334 }, // Centre de la France
        zoom: 6,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      };
      
      // Initialiser la carte Google Maps
      map = new google.maps.Map(mapElement, mapOptions);
      
      // Créer une info-bulle unique qui sera réutilisée
      infoWindow = new google.maps.InfoWindow();
      
      // Initialiser le geocoder pour convertir les adresses en coordonnées
      geocoder = new google.maps.Geocoder();
      
      mapInitialized = true;
      
      // Si des stores sont déjà disponibles, les afficher
      if (stores.length > 0) {
        updateMarkers();
      }
      
      // Si on a déjà une recherche, mettre à jour le cercle de rayon
      if (searchLocation) {
        updateSearchRadius();
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte:", error);
    }
  }
  
  function updateSearchRadius() {
    if (!geocoder || !searchLocation) return;
    
    // Supprimer le cercle existant
    if (circle) {
      circle.setMap(null);
    }
    
    // Supprimer le marqueur central existant
    if (centerMarker) {
      centerMarker.setMap(null);
    }
    
    // Obtenir les coordonnées de l'emplacement de recherche
    geocoder.geocode({ 'address': searchLocation + ', France' }, function(results, status) {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        const location = results[0].geometry.location;
        
        // Créer un marqueur pour le centre de recherche
        centerMarker = new google.maps.Marker({
          position: location,
          map: map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: "#4285F4",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2
          },
          title: `Centre de recherche: ${searchLocation}`,
          zIndex: 1000
        });
        
        // Créer un cercle pour représenter le rayon de recherche
        circle = new google.maps.Circle({
          map: map,
          center: location,
          radius: searchRadius * 1000, // Convertir km en mètres
          fillColor: '#4285F4',
          fillOpacity: 0.1,
          strokeColor: '#4285F4',
          strokeOpacity: 0.8,
          strokeWeight: 1
        });
        
        // Si aucun magasin n'a été trouvé, centrer sur le cercle
        if (stores.length === 0) {
          map.fitBounds(circle.getBounds());
        }
      }
    });
  }
  
  function updateMarkers() {
    // Vérifier que la carte est initialisée et que Google Maps est disponible
    if (!map || !google || !google.maps) {
      console.error("La carte n'est pas initialisée ou Google Maps n'est pas disponible");
      return;
    }
    
    try {
      // Supprimer les anciens marqueurs
      if (markers.length > 0) {
        markers.forEach(marker => {
          if (marker) marker.setMap(null);
        });
        markers = [];
      }
      
      // Si aucun magasin, centrer sur la France
      if (stores.length === 0) {
        if (!searchLocation) {
          map.setCenter({ lat: 46.603354, lng: 1.888334 });
          map.setZoom(6);
        }
        return;
      }
      
      // Créer les limites pour ajuster automatiquement la vue
      const bounds = new google.maps.LatLngBounds();
      
      // Ajouter les nouveaux marqueurs
      stores.forEach(store => {
        const position = { lat: store.lat, lng: store.lng };
        
        const marker = new google.maps.Marker({
          position: position,
          map: map,
          title: store.name,
          animation: google.maps.Animation.DROP
        });
        
        // Contenu de l'info-bulle, inclure la distance si disponible
        let distanceInfo = '';
        if (store.distance) {
          distanceInfo = `<p class="store-distance">Distance: ${store.distance.toFixed(1)} km</p>`;
        }
        
        const contentString = `
          <div class="info-window">
            <h3>${store.name}</h3>
            <p>${store.address}<br>${store.zipCode} ${store.city}</p>
            ${distanceInfo}
            </div>
        `;
        //<a href="https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}" target="_blank">Itinéraire</a>
          
        // Ajouter un événement de clic pour afficher l'info-bulle
        marker.addListener("click", () => {
          infoWindow.setContent(contentString);
          infoWindow.open(map, marker);
        });
        
        // Ajouter le marqueur au tableau
        markers.push(marker);
        
        // Étendre les limites pour inclure ce marqueur
        bounds.extend(position);
      });
      
      // Ajuster la vue pour montrer tous les marqueurs
      map.fitBounds(bounds);
      
      // Si un seul magasin, ajuster le zoom
      if (stores.length === 1) {
        map.setZoom(15);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour des marqueurs:", error);
    }
  }
</script>

<div class="map-container">
  <div bind:this={mapElement} class="map-element"></div>
  
  {#if stores.length === 0 && !searchLocation}
    <div class="map-placeholder">
      <p class="map-info">
        Aucun magasin à afficher. Veuillez effectuer une recherche.
      </p>
    </div>
  {/if}
</div>

<style>
  .map-container {
  z-index:1;
    width: 100%;
    height: calc(100% - 60px);
    background-color: #f0f0f0;
    border-radius: 4px;
    position: relative;
  }
  @media only screen and (max-width: 768px) {
    .map-container {
        min-height: 60vh;
      }
  }

  .map-element {
    width: 100%;
    height: 100%;
    border-radius: 4px;
  }
  
  .map-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: #666;
    z-index: 1000;
    background-color: rgba(255, 255, 255, 1);
    pointer-events: none;
  }
  
  .map-info {
    max-width: 80%;
    font-size: 14px;
  }
  
  /* Ces styles seront appliqués globalement, mais affecteront le contenu des info-bulles */
  :global(.info-window h3) {
    margin-top: 0;
    margin-bottom: 8px;
    font-size: 16px;
  }
  
  :global(.info-window p) {
    margin-top: 0;
    margin-bottom: 8px;
  }
  
  :global(.store-distance) {
    color: #4285F4;
    font-weight: bold;
  }
  
  :global(.info-window a) {
    color: #4285F4;
    text-decoration: none;
    font-weight: bold;
  }
  
  :global(.info-window a:hover) {
    text-decoration: underline;
  }
</style>